import {
	ApolloClient,
	ApolloLink,
	createHttpLink,
	InMemoryCache,
	Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";

import { getAnonymousToken } from "../util/anonymousTokenStorage";
import { getDOLToken } from "../util/dolTokenStorage";
import { getToken } from "./Amplify";

const httpLink = createHttpLink({
	uri: process.env.REACT_APP_GRAPHQL_URL,
});

const authLink = setContext(async (_, prevContext) => {
	const cognitoToken = await getToken();
	const anonymousToken = getAnonymousToken();
	const dolToken = getDOLToken();
	const token = anonymousToken || dolToken || cognitoToken;
	const { headers, noAuth } = prevContext;
	if (!noAuth) {
		return {
			...prevContext,
			headers: {
				...headers,
				authorization: token ? `Bearer ${token}` : "",
			},
		};
	}
});

// Custom retry link that handles all errors (not just network errors)
// Exported for testing
// Retries with exponential backoff: 1s, 2s, 4s, 8s, 16s (~31s total)
// Context options:
//   - maxRetries: number of retries (default 0, no retries)
//   - onRetry: callback(attemptNumber) called on each attempt (0 for first, 1+ for retries)
const retryLink = new ApolloLink((operation, forward) => {
	const { maxRetries = 0, onRetry } = operation.getContext();
	if (maxRetries === 0) return forward(operation);

	const operationName = operation.operationName || "unknown";

	return new Observable((observer) => {
		let attempt = 0;
		let subscription;
		let retrying = false;

		const tryRequest = () => {
			retrying = false;
			onRetry?.(attempt);
			console.debug(`[RetryLink] ${operationName}: attempt ${attempt} of ${maxRetries}`);
			subscription = forward(operation).subscribe({
				next: (result) => {
					// Check for GraphQL errors (query failed but returned error response)
					if (result.errors?.length && attempt < maxRetries) {
						const delay = Math.pow(2, attempt) * 1000;
						console.warn(
							`[RetryLink] ${operationName}: GraphQL error, retrying in ${delay}ms`,
							result.errors,
						);
						attempt++;
						retrying = true;
						setTimeout(tryRequest, delay);
					} else {
						if (result.errors?.length) {
							console.warn(
								`[RetryLink] ${operationName}: GraphQL error, max retries reached`,
								result.errors,
							);
						} else {
							console.debug(`[RetryLink] ${operationName}: success`);
						}
						observer.next(result);
					}
				},
				error: (error) => {
					// Network errors or other failures
					if (attempt < maxRetries) {
						const delay = Math.pow(2, attempt) * 1000;
						console.warn(
							`[RetryLink] ${operationName}: network error, retrying in ${delay}ms`,
							error.message,
						);
						attempt++;
						retrying = true;
						setTimeout(tryRequest, delay);
					} else {
						console.warn(
							`[RetryLink] ${operationName}: network error, max retries reached`,
							error.message,
						);
						observer.error(error);
					}
				},
				complete: () => {
					// Only complete if we're not retrying
					if (!retrying) {
						observer.complete();
					}
				},
			});
		};

		tryRequest();

		return () => subscription?.unsubscribe();
	});
});

export { retryLink };

export default new ApolloClient({
	link: ApolloLink.from([retryLink, authLink, httpLink]),
	cache: new InMemoryCache(),
});
